import clsx from 'clsx'
import React from 'react'

import { Checkbox } from '@Components/FormElements/UI/Checkbox'
import { ImageV2 } from '@Components/Image'
import { Link } from '@Components/Link'
import { Wordbook } from '@Components/Wordbook'
import { UpSellTooltip } from '@Domains/Product/Features/Display/UpSellTooltip/UpSellTooltip'
import { AddToCartProductTactic, WordbookFragmentFragment } from '@Generated/graphql'
import { CrossSellProduct } from '@Globals/CMS/ProductHelpers'
import { useTranslation } from '@Globals/Translations'

import styles from './UpSellItem.module.scss'

export const UpSellItem = ({
  id,
  offer,
  onChange,
  product,
  productHeader,
  selected,
  size = 'small',
  type,
}: {
  id?: string
  offer?: AddToCartProductTactic
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  product?: CrossSellProduct
  productHeader?: WordbookFragmentFragment
  selected?: boolean
  size?: 'small' | 'large'
  type?: string
}) => {
  const { t } = useTranslation()
  const checkboxRef = React.useRef<HTMLInputElement | null>(null)

  if (!product || !product.primaryAsset?.url) {
    return null
  }

  const {
    id: productID,
    masterVariant,
    name,
    url,
  } = product
  const componentID = id || productID

  const price = offer?.price || masterVariant?.price || null
  if (!price) {
    return null
  }

  const hasDiscount = !!price?.discount
  const isFree = price?.value === 0 || price?.discount?.value === 0 || false
  const label = t('products:ALSO_ADD_PRODUCT_TO_CART', { arg1: name })
  const priceLabel = isFree ? t('header:FREE_LABEL') : price?.format

  // @ts-ignore
  const upsellType = type || product?.masterVariant?.type?.value.key

  let img = (
    <ImageV2
      alt={name || ''}
      height={size === 'large' ? 90 : 100}
      objectFit="contain"
      src={product.primaryAsset.url}
      width={size === 'large' ? 70 : 100}
    />
  )

  if (url) {
    img = (
      <Link
        analytics={{
          dest_page_type: 'product',
          dest_page_type_id: product.id,
        }}
        href={url}
        onClick={e => e.stopPropagation()}
      >
        {img}
      </Link>
    )
  }

  return (
    <div
      key={componentID}
      className={clsx(styles.item, styles[size])}
      onClick={(e) => {
        console.log(e.target, e.currentTarget, checkboxRef.current)
        checkboxRef.current?.click()
      }}
      title={name || ''}
    >
      <div className={styles.image}>
        {img}
      </div>
      <div className={styles.content}>
        <div className={styles.description}>
          {productHeader && (
            <div className={styles.productHeader}>
              <Wordbook wordbook={productHeader} />
            </div>
          )}

          <div className={styles.name}>
            {name}

            {upsellType === 'engrave' && (
              <UpSellTooltip>
                {t('products:TOOL_TIP_ENGRAVING')}
              </UpSellTooltip>
            )}

            {upsellType === 'deboss' && (
              <UpSellTooltip>
                {t('products:TOOL_TIP_DEBOSS')}
              </UpSellTooltip>
            )}

            {upsellType === 'giftWrappings' && (
              <UpSellTooltip>
                {t('products:TOOL_TIP_GIFT_WRAPPING')}
              </UpSellTooltip>
            )}
          </div>
          <div className={styles.priceWrapper}>
            {!isFree && '+ '}
            {hasDiscount && <span className={styles.beforePrice}>{`${price?.format}`}</span>}
            <span className={`${styles.price} ${hasDiscount ? styles.discount : ''}`}>
              {`${!isFree && hasDiscount ? price?.discount?.format : priceLabel}`}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.checkboxWrapper}>
        <Checkbox
          aria-label={label}
          checked={selected}
          id={`crossSell${componentID}`}
          indicatorClassName={styles.checkbox}
          label=""
          onChange={onChange}
          ref={checkboxRef}
          title={label}
          value={componentID}
          withoutEmptySpan
        />
      </div>
    </div>
  )
}
